import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { LocationFacade } from '@app/modules/location/facade/location.facade';
import { environment } from '@environments/environment';
import { Subject } from 'rxjs';
import { take, map } from 'rxjs/operators';
import * as deepEqual from 'fast-deep-equal';
import { FilterChangeEvent, FilterDialogState, FilterProps } from '@app/modules/location/models/filter-dialog.model';
import { EntityResource, ToggleFilterEvent } from '@app/store/filters/models/filters.model';
import { TranslateService } from '@livemaps/i18n';
import { Translations } from '@app/core/services/i18n/translations.service';

@Component({
  selector: 'app-filter-dialog',
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterDialogComponent implements OnInit, OnDestroy {
  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    public locationFacade: LocationFacade,
    public translations: Translations,
    public translateService: TranslateService
  ) {}
  disableButtons = true;
  filterState: FilterDialogState;
  defaultFilter: FilterDialogState;
  divisionsWithLocations: EntityResource[];
  destroyed$ = new Subject();
  isZonarUser: boolean;
  initialPowerOnFilter: boolean;
  // legacyAccountsEnabled: boolean = environment.legacyAccountsEnabled;

  ngOnInit() {
    this.locationFacade
      .getUserPermissions()
      .pipe(take(1))
      .subscribe(perm => {
        this.isZonarUser = perm.isZonarUser;
      });

    this.locationFacade
      .getAllFilters()
      .pipe(take(1))
      .subscribe(filters => {
        const { filter, defaultFilter } = filters;

        this.initialPowerOnFilter = filter.powerOn;
        this.filterState = <FilterDialogState>{
          powerOn: filter.powerOn,
          company: filter.company?.id,
          divisions: filter.divisions?.map(d => d.id),
          // locations: filter.locations?.map(l => l.id),
          siteGroup: filter.siteGroup
        };

        this.defaultFilter = <FilterDialogState>{
          powerOn: defaultFilter.powerOn,
          company: defaultFilter?.company?.id,
          divisions: defaultFilter?.divisions?.map(d => d.id),
          // locations: defaultFilter?.locations.map(l => l.id),
          siteGroup: defaultFilter?.siteGroup
        };

        this.disableButtons = deepEqual(this.filterState, this.defaultFilter);
      });
  }

  getCurrentCompany() {
    return this.locationFacade.getAllFilters().pipe(
      map(filters => {
        return filters.filter.company;
      })
    );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  clearFilters() {
    this.getCurrentCompany()
      .pipe(take(1))
      .subscribe(company => {
        this.locationFacade.clearFilters();
        this.locationFacade.setCompanyFilter(company.id);
        this.locationFacade.setAssets({
          companyId: company.id,
          pageSize: environment.liveUpdate.pageSize
        });
        this.close();
      });
  }

  applyFilters() {
    this.locationFacade.applyFilters(this.filterState);
    this.close();
  }

  toggleFilterChange(event: ToggleFilterEvent) {
    this.filterState = { ...this.filterState, [event.filter]: event.toggled };
    this.disableButtons = false;
  }

  dropdownFilterChange(event: FilterChangeEvent) {
    const { entity, value } = event;
    this.filterState = { ...this.filterState, [entity]: value };

    switch (entity) {
      case FilterProps.COMPANY:
        const companyValue = Array.isArray(value) ? value[0] : value;
        this.filterState = { ...this.filterState, company: companyValue };
        break;
      case FilterProps.DIVISIONS:
        // const divisionIds = Array.isArray(value) ? value : [value];
        // this.updateFilterLocations(divisionIds);
        // get an array of the unique divisions after this new division is added
        const uniqueDivisions = [...new Set([...this.filterState.divisions, ...value])];
        this.filterState = { ...this.filterState, divisions: uniqueDivisions };
        break;
    }
    this.disableButtons = false;
  }
}
